<template>
  <div>
    <!-- ==================== Hero Section ==================== -->
    <section class="hero">
      <div class="container hero-container">
        <div class="hero-text">
          <h1>{{ $t('home.title') }}</h1>
          <p class="hero-subtitle">{{ $t('home.subtitle') }}</p>
          <p class="hero-desc">{{ $t('home.heroDesc') }}</p>
          <div class="hero-actions">
            <router-link to="/download" class="btn btn-primary btn-lg">
              <span>⬇</span> {{ $t('home.download') }}
            </router-link>
            <a href="#demo" class="btn btn-outline btn-lg">{{ $t('home.watchDemo') }}</a>
          </div>
        </div>
        <div class="hero-visual">
          <div class="hero-card">
            <div class="hero-card-header">
              <span class="dot red"></span>
              <span class="dot yellow"></span>
              <span class="dot green"></span>
              <span class="card-title">{{ $t('home.heroCardTitle') }}</span>
            </div>
            <div class="hero-card-body">
              <div class="chat-line ai">
                <span class="tag">AI</span>
                <span>{{ $t('home.heroCardAi1') }}</span>
              </div>
              <div class="chat-line user">
                <span class="tag">{{ $t('home.heroCardYou') }}</span>
                <span>{{ $t('home.heroCardQuestion') }}</span>
              </div>
              <div class="chat-line ai">
                <span class="tag">AI</span>
                <span>{{ $t('home.heroCardAnswer') }}<br><small>{{ $t('home.heroCardSource') }}</small></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== Demo Video ==================== -->
    <section class="demo" id="demo">
      <div class="container">
        <h2 class="section-title">{{ $t('home.demoTitle') }}</h2>
        <p class="section-subtitle">{{ $t('home.demoSubtitle') }}</p>
        <div class="demo-frame">
          <iframe
            :src="demoVideoSrc"
            :title="$t('home.demoTitle')"
            allow="autoplay; picture-in-picture"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
        <p class="demo-caption">{{ $t('home.demoCaption') }}</p>
      </div>
    </section>

    <!-- ==================== Editions ==================== -->
    <section class="editions-section" id="editions">
      <div class="container">
        <h2 class="section-title">{{ $t('home.editionsTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('home.editionsSubtitle') }}</p>
        <div class="editions-grid">
          <div class="edition-card local">
            <span class="edition-tag">{{ $t('home.localTag') }}</span>
            <h3>{{ $t('home.localTitle') }}</h3>
            <p class="edition-desc">{{ $t('home.localDesc') }}</p>
            <ul>
              <li v-for="i in 4" :key="i">{{ $t(`home.localFeature${i}`) }}</li>
            </ul>
            <router-link to="/purchase" class="btn btn-primary">{{ $t('home.localCta') }}</router-link>
          </div>
          <div class="edition-card cloud">
            <span class="edition-tag">{{ $t('home.cloudTag') }}</span>
            <h3>{{ $t('home.cloudTitle') }}</h3>
            <p class="edition-desc">{{ $t('home.cloudDesc') }}</p>
            <ul>
              <li v-for="i in 4" :key="i">{{ $t(`home.cloudFeature${i}`) }}</li>
            </ul>
            <router-link to="/purchase" class="btn btn-primary">{{ $t('home.cloudCta') }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 3 Core Values ==================== -->
    <section class="values" id="values">
      <div class="container">
        <h2 class="section-title">{{ $t('home.valueTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('home.valueSubtitle') }}</p>
        <div class="values-grid">
          <div class="value-card">
            <div class="value-icon rag-icon">
              <svg viewBox="0 0 48 48" width="48" height="48"><path fill="currentColor" d="M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm-2 30h-4V20h4v14zm6 0h-4V14h4v20z"/></svg>
            </div>
            <h3>{{ $t('home.valueRag') }}</h3>
            <p>{{ $t('home.valueRagDesc') }}</p>
            <div class="value-stat">{{ $t('home.valueRagStat') }}</div>
          </div>
          <div class="value-card">
            <div class="value-icon private-icon">
              <svg viewBox="0 0 48 48" width="48" height="48"><path fill="currentColor" d="M24 4C18.48 4 14 8.48 14 14v4H8v26h32V18h-6v-4c0-5.52-4.48-10-10-10zm-6 14V14c0-3.32 2.68-6 6-6s6 2.68 6 6v4H18z"/></svg>
            </div>
            <h3>{{ $t('home.valuePrivate') }}</h3>
            <p>{{ $t('home.valuePrivateDesc') }}</p>
            <div class="value-stat">{{ $t('home.valuePrivateStat') }}</div>
          </div>
          <div class="value-card">
            <div class="value-icon mcp-icon">
              <svg viewBox="0 0 48 48" width="48" height="48"><path fill="currentColor" d="M38 6H10C7.8 6 6 7.8 6 10v28c0 2.2 1.8 4 4 4h28c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4zm-8 24h-4v-4h4v4zm0-8h-4v-4h4v4zm0-8h-4v-4h4v4z"/></svg>
            </div>
            <h3>{{ $t('home.valueMcp') }}</h3>
            <p>{{ $t('home.valueMcpDesc') }}</p>
            <div class="value-stat">{{ $t('home.valueMcpStat') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== Honor Section ==================== -->
    <section class="honor">
      <div class="container">
        <h2 class="section-title">{{ $t('home.honor') }}</h2>
        <p class="honor-desc">{{ $t('home.honorDesc') }}</p>
        <div class="honor-grid">
          <div class="honor-card">
            <img src="/img/arm.png" alt="ARM" class="honor-image">
            <p class="honor-name">ARM</p>
            <p class="honor-award">{{ $t('home.honorArm') }}</p>
          </div>
          <div class="honor-card">
            <img src="/img/intel.png" alt="Intel" class="honor-image">
            <p class="honor-name">Intel</p>
            <p class="honor-award">{{ $t('home.honorIntel') }}</p>
          </div>
          <div class="honor-card">
            <img src="/img/lenovo.png" alt="Lenovo" class="honor-image">
            <p class="honor-name">Lenovo</p>
            <p class="honor-award">{{ $t('home.honorLenovo') }}</p>
          </div>
          <div class="honor-card">
            <img src="/img/qualcomm.svg" alt="Qualcomm" class="honor-image">
            <p class="honor-name">Qualcomm</p>
            <p class="honor-award">{{ $t('home.honorQualcomm') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== Nine Outputs ==================== -->
    <section class="outputs" id="outputs">
      <div class="container">
        <h2 class="section-title">{{ $t('home.outputsTitle') }}</h2>
        <p class="section-subtitle dark">{{ $t('home.outputsSubtitle') }}</p>
        <div class="outputs-grid">
          <div v-for="key in outputKeys" :key="key" class="output-card">
            <span class="output-icon">{{ outputIcons[key] }}</span>
            <h3>{{ $t(`home.output${key}`) }}</h3>
            <p>{{ $t(`home.output${key}Desc`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== Use Cases ==================== -->
    <section class="use-cases">
      <div class="container">
        <h2 class="section-title">{{ $t('home.useCases') }}</h2>
        <p class="section-subtitle dark">{{ $t('home.useCasesDesc') }}</p>
        <div class="cases-grid">
          <div class="case-card">
            <h3>{{ $t('home.case1') }}</h3>
            <p class="case-tagline">{{ $t('home.case1Desc') }}</p>
            <p class="case-detail">{{ $t('home.case1Detail') }}</p>
          </div>
          <div class="case-card">
            <h3>{{ $t('home.case2') }}</h3>
            <p class="case-tagline">{{ $t('home.case2Desc') }}</p>
            <p class="case-detail">{{ $t('home.case2Detail') }}</p>
          </div>
          <div class="case-card">
            <h3>{{ $t('home.case3') }}</h3>
            <p class="case-tagline">{{ $t('home.case3Desc') }}</p>
            <p class="case-detail">{{ $t('home.case3Detail') }}</p>
          </div>
          <div class="case-card">
            <h3>{{ $t('home.case4') }}</h3>
            <p class="case-tagline">{{ $t('home.case4Desc') }}</p>
            <p class="case-detail">{{ $t('home.case4Detail') }}</p>
          </div>
          <div class="case-card">
            <h3>{{ $t('home.case5') }}</h3>
            <p class="case-tagline">{{ $t('home.case5Desc') }}</p>
            <p class="case-detail">{{ $t('home.case5Detail') }}</p>
          </div>
          <div class="case-card">
            <h3>{{ $t('home.case6') }}</h3>
            <p class="case-tagline">{{ $t('home.case6Desc') }}</p>
            <p class="case-detail">{{ $t('home.case6Detail') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== CTA ==================== -->
    <section class="cta-section">
      <div class="container">
        <h2>{{ $t('home.ctaTitle') }}</h2>
        <p>{{ $t('home.ctaDesc') }}</p>
        <div class="cta-actions">
          <router-link to="/download" class="btn btn-primary btn-lg">
            <span>⬇</span> {{ $t('home.ctaStart') }}
          </router-link>
          <a href="mailto:wmx@turingevo.com" class="btn btn-outline btn-lg">{{ $t('home.ctaContact') }}</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      outputKeys: ['Report', 'Mindmap', 'Ppt', 'Wiki', 'Audio', 'Video', 'Quiz', 'Cards', 'Infographic'],
      outputIcons: {
        Report: '📊',
        Mindmap: '🧠',
        Ppt: '📽️',
        Wiki: '📚',
        Audio: '🎙️',
        Video: '🎬',
        Quiz: '📝',
        Cards: '🃏',
        Infographic: '📈'
      },
      // 英文演示视频上线后填入 YouTube 视频 ID
      youtubeVideoId: ''
    }
  },
  computed: {
    isZh() {
      return this.$i18n.locale === 'zh-CN'
    },
    demoVideoSrc() {
      if (!this.isZh && this.youtubeVideoId) {
        return `https://www.youtube.com/embed/${this.youtubeVideoId}`
      }
      return 'https://player.bilibili.com/player.html?bvid=BV1nUxB65EHS&page=1&high_quality=1&danmaku=0&autoplay=0'
    }
  }
}
</script>

<style scoped>
/* ============================
   Hero
   ============================ */
.hero {
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  padding: 80px 20px 100px;
  overflow: hidden;
}

.hero-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.hero-text h1 {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #667eea 0%, #ff5f5f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #e0e0ff;
}

.hero-desc {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #a0a0cc;
  margin-bottom: 32px;
  max-width: 500px;
}

.hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-lg {
  padding: 14px 36px;
  font-size: 1.1rem;
}

.btn-outline {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-outline:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.15);
  transform: translateY(-2px);
}

/* Hero Card Visual */
.hero-visual {
  display: flex;
  justify-content: center;
}

.hero-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.hero-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot.red { background: #ff5f5f; }
.dot.yellow { background: #ffbd2e; }
.dot.green { background: #29c840; }

.card-title {
  margin-left: auto;
  font-size: 0.8rem;
  color: #8888bb;
}

.hero-card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chat-line {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.9rem;
  line-height: 1.5;
}

.chat-line .tag {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.chat-line.user .tag {
  background: rgba(102, 126, 234, 0.3);
  color: #8899ff;
}

.chat-line.ai .tag {
  background: rgba(255, 95, 95, 0.3);
  color: #ff8888;
}

.chat-line small {
  color: #8888bb;
  font-size: 0.75rem;
}

/* ============================
   Editions
   ============================ */
.editions-section {
  padding: 80px 20px;
  background: #f8f9fa;
}

.editions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  max-width: 960px;
  margin: 40px auto 0;
}

.edition-card {
  background: white;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.edition-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.edition-card.local {
  border-top: 4px solid #667eea;
}

.edition-card.cloud {
  border-top: 4px solid #22b8cf;
}

.edition-tag {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #eef0ff;
  color: #667eea;
  margin-bottom: 16px;
}

.edition-card.cloud .edition-tag {
  background: #e6f8fb;
  color: #0b7285;
}

.edition-card h3 {
  font-size: 1.4rem;
  color: #222;
  margin-bottom: 10px;
}

.edition-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
}

.edition-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
}

.edition-card li {
  padding: 9px 0;
  font-size: 0.95rem;
  color: #444;
  line-height: 1.5;
  border-bottom: 1px solid #f0f0f0;
}

.edition-card li:last-child {
  border-bottom: none;
}

.edition-card li::before {
  content: '✓';
  color: #2f9e44;
  font-weight: 700;
  margin-right: 8px;
}

.edition-card .btn {
  margin-top: auto;
  width: 100%;
  text-align: center;
}

/* ============================
   3 Core Values
   ============================ */
.values {
  padding: 100px 20px;
  background: white;
}

.section-subtitle.dark {
  color: #666;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.value-card {
  text-align: center;
  padding: 48px 32px;
  border-radius: 16px;
  background: #f8f9fa;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.value-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
  border-color: #667eea;
}

.value-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 24px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.rag-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.private-icon { background: linear-gradient(135deg, #ff5f5f, #ff9a44); }
.mcp-icon { background: linear-gradient(135deg, #29c840, #20a0ff); }

.value-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #222;
}

.value-card p {
  color: #666;
  line-height: 1.7;
  font-size: 0.95rem;
  margin-bottom: 20px;
}

.value-stat {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 16px;
  background: white;
  border-radius: 8px;
  color: #667eea;
  display: inline-block;
}

/* ============================
   Demo Video
   ============================ */
.demo {
  padding: 60px 20px 100px;
  background: linear-gradient(180deg, #24243e 0%, #0f0c29 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.demo .section-title {
  color: white;
}

.demo-frame {
  max-width: 900px;
  margin: 0 auto;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.demo-frame iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.demo-caption {
  text-align: center;
  color: #a0a0cc;
  font-size: 0.9rem;
  margin-top: 20px;
}

/* ============================
   Nine Outputs
   ============================ */
.outputs {
  padding: 100px 20px;
  background: #f8f9fa;
}

.outputs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 40px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.output-card {
  background: white;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.output-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15);
}

.output-icon {
  display: block;
  font-size: 2rem;
  margin-bottom: 12px;
}

.output-card h3 {
  font-size: 1.15rem;
  color: #222;
  margin-bottom: 8px;
}

.output-card p {
  font-size: 0.92rem;
  line-height: 1.6;
  color: #666;
}

/* ============================
   Use Cases
   ============================ */
.use-cases {
  padding: 100px 20px;
  background: white;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 40px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.case-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 32px 24px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
}

.case-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #667eea;
}

.case-card h3 {
  font-size: 1.3rem;
  margin-bottom: 8px;
  color: #222;
}

.case-tagline {
  color: #667eea;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0 0 12px;
}

.case-detail {
  color: #666;
  line-height: 1.7;
  font-size: 0.9rem;
  margin: 0;
}


/* ============================
   Honor
   ============================ */
.honor {
  padding: 80px 20px;
  background: white;
  text-align: center;
}

.honor-desc {
  font-size: 1.05rem;
  color: #666;
  margin-bottom: 8px;
}

.honor-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 48px;
  max-width: 960px;
  margin-left: auto;
  margin-right: auto;
}

.honor-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.honor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}

.honor-image {
  width: 100%;
  max-width: 160px;
  height: auto;
  border-radius: 6px;
}

.honor-name {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin-top: 4px;
}

.honor-award {
  font-size: 0.8rem;
  color: #888;
  line-height: 1.4;
}

/* ============================
   CTA
   ============================ */
.cta-section {
  padding: 100px 20px;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  text-align: center;
}

.cta-section h2 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.cta-section p {
  font-size: 1.1rem;
  color: #a0a0cc;
  margin-bottom: 40px;
}

.cta-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.cta-section .btn-outline {
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
}

.cta-section .btn-outline:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.15);
}

/* ============================
   Responsive
   ============================ */
@media (max-width: 968px) {
  .hero-container {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-text h1 {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  .values-grid {
    grid-template-columns: 1fr;
  }

  .cases-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .outputs-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .editions-grid {
    grid-template-columns: 1fr;
  }

  .honor-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-text h1 {
    font-size: 2rem;
  }

  .hero-desc {
    font-size: 1rem;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .cases-grid {
    grid-template-columns: 1fr;
  }

  .outputs-grid {
    grid-template-columns: 1fr;
  }

  .honor-grid {
    grid-template-columns: 1fr;
  }
}
</style>
